import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { log } from '@/lib/logger';

/**
 * Collection name for program assignments
 */
const PROGRAM_ASSIGNMENTS_COLLECTION = 'program_assignments';

/**
 * Program assignment types - simplified approach
 */
export interface ProgramAssignment {
  programId: string;           // Program identifier (usually user ID who owns the program)
  programName: string;         // Display name for the program
  assignedUsers: string[];     // Array of user IDs assigned to this program
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  lastModifiedBy: string;      // User ID who last modified
}

interface CreateProgramAssignmentData {
  programId: string;
  programName: string;
  assignedUsers: string[];
}

export interface UpdateProgramAssignmentData {
  programName?: string;
  assignedUsers?: string[];
}

/**
 * Service for managing program assignments - simplified approach
 */
class ProgramAssignmentService {
  
  /**
   * Get all program assignments
   */
  async getAllProgramAssignments(): Promise<ProgramAssignment[]> {
    try {
      const assignmentsRef = collection(db, PROGRAM_ASSIGNMENTS_COLLECTION);
      const snapshot = await getDocs(assignmentsRef);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ProgramAssignment;
      });
    } catch (error) {
      log.error('Error fetching program assignments:', error);
      throw new Error('Failed to fetch program assignments');
    }
  }

  /**
   * Get program assignment by program ID
   */
  async getProgramAssignment(programId: string): Promise<ProgramAssignment | null> {
    try {
      const assignmentRef = doc(db, PROGRAM_ASSIGNMENTS_COLLECTION, programId);
      const snapshot = await getDoc(assignmentRef);
      
      if (!snapshot.exists()) {
        return null;
      }

      const data = snapshot.data();
      return {
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ProgramAssignment;
    } catch (error) {
      log.error('Error fetching program assignment:', error);
      throw new Error(`Failed to fetch program assignment for ${programId}`);
    }
  }

  /**
   * Create or update program assignment
   */
  async upsertProgramAssignment(
    programId: string,
    data: CreateProgramAssignmentData,
    modifiedBy: string
  ): Promise<ProgramAssignment> {
    try {
      const assignmentRef = doc(db, PROGRAM_ASSIGNMENTS_COLLECTION, programId);
      const now = new Date();
      
      const assignmentData = {
        programId,
        programName: data.programName,
        assignedUsers: data.assignedUsers,
        updatedAt: Timestamp.fromDate(now),
        lastModifiedBy: modifiedBy,
      };

      // Check if document exists to determine if we need to set createdAt
      const existingDoc = await getDoc(assignmentRef);
      if (!existingDoc.exists()) {
        (assignmentData as any).createdAt = Timestamp.fromDate(now);
      }

      await setDoc(assignmentRef, assignmentData, { merge: true });

      return {
        ...assignmentData,
        createdAt: existingDoc.exists()
          ? existingDoc.data()?.createdAt?.toDate() || now
          : now,
        updatedAt: now
      } as ProgramAssignment;
    } catch (error) {
      log.error('Error upserting program assignment:', error);
      throw new Error(`Failed to save program assignment for ${programId}`);
    }
  }

  /**
   * Update program assignment
   */
  async updateProgramAssignment(
    programId: string,
    updates: UpdateProgramAssignmentData,
    modifiedBy: string
  ): Promise<void> {
    try {
      const assignmentRef = doc(db, PROGRAM_ASSIGNMENTS_COLLECTION, programId);
      
      await updateDoc(assignmentRef, {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date()),
        lastModifiedBy: modifiedBy
      });
    } catch (error) {
      log.error('Error updating program assignment:', error);
      throw new Error(`Failed to update program assignment for ${programId}`);
    }
  }

  /**
   * Delete program assignment
   */
  async deleteProgramAssignment(programId: string): Promise<void> {
    try {
      const assignmentRef = doc(db, PROGRAM_ASSIGNMENTS_COLLECTION, programId);
      await deleteDoc(assignmentRef);
    } catch (error) {
      log.error('Error deleting program assignment:', error);
      throw new Error(`Failed to delete program assignment for ${programId}`);
    }
  }

  /**
   * Bulk update program assignments
   */
  async bulkUpdateProgramAssignments(
    assignments: Array<{ programId: string; data: CreateProgramAssignmentData }>,
    modifiedBy: string
  ): Promise<void> {
    try {
      // Use individual updates instead of batch for better error handling
      const updatePromises = assignments.map(({ programId, data }) =>
        this.upsertProgramAssignment(programId, data, modifiedBy)
      );

      await Promise.all(updatePromises);
    } catch (error) {
      log.error('Error bulk updating program assignments:', error);
      throw new Error('Failed to bulk update program assignments');
    }
  }

  /**
   * Get programs for a specific user
   */
  async getProgramsForUser(userId: string): Promise<ProgramAssignment[]> {
    try {
      const assignmentsRef = collection(db, PROGRAM_ASSIGNMENTS_COLLECTION);
      const q = query(assignmentsRef, where('assignedUsers', 'array-contains', userId));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ProgramAssignment;
      });
    } catch (error) {
      log.error('Error fetching programs for user:', error);
      throw new Error(`Failed to fetch programs for user ${userId}`);
    }
  }

  /**
   * Subscribe to all program assignments
   */
  subscribeToAllProgramAssignments(
    callback: (assignments: ProgramAssignment[]) => void
  ): () => void {
    const assignmentsRef = collection(db, PROGRAM_ASSIGNMENTS_COLLECTION);
    
    return onSnapshot(assignmentsRef, (snapshot) => {
      const assignments = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ProgramAssignment;
      });
      
      callback(assignments);
    }, (error) => {
      log.error('Error in program assignments subscription:', error);
    });
  }

  /**
   * Subscribe to programs for a specific user
   */
  subscribeToUserPrograms(
    userId: string,
    callback: (assignments: ProgramAssignment[]) => void
  ): () => void {
    const assignmentsRef = collection(db, PROGRAM_ASSIGNMENTS_COLLECTION);
    const q = query(assignmentsRef, where('assignedUsers', 'array-contains', userId));
    
    return onSnapshot(q, (snapshot) => {
      const assignments = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ProgramAssignment;
      });
      
      callback(assignments);
    }, (error) => {
      log.error('Error in user programs subscription:', error);
    });
  }
}

// Export singleton instance
export const programAssignmentService = new ProgramAssignmentService();